<template>
  <div class="suppliers-page space-y-5">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Suppliers</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
          {{ suppliers ? suppliers.length : 0 }} supplier{{ suppliers?.length !== 1 ? 's' : '' }}
        </p>
      </div>
      <router-link
        :to="{ name: 'OdooContacts' }"
        class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        <span class="material-icons text-[18px]" aria-hidden="true">sync_alt</span>
        Odoo Sync
      </router-link>
    </div>

    <!-- Load error (nothing fetched yet) -->
    <ErrorState
      v-if="fetchError && !suppliers"
      title="Couldn't load suppliers"
      :message="fetchError"
      @retry="subscribe"
    />

    <!-- Suppliers Table -->
    <template v-else>
      <AlertBanner
        v-if="fetchError"
        type="error"
        message="Couldn't refresh suppliers — showing the last loaded list."
        :dismissible="false"
        class="mb-4"
      />
      <DataTable
      :data="suppliers || []"
      :columns="tableColumns"
      :loading="!suppliers"
      :searchable="true"
      :show-index="true"
      :pagination="true"
      loading-text="Loading suppliers..."
      empty-text="No suppliers found"
    >
      <template #cell-suppliername="{ row }">
        <router-link
          :to="{ name: 'SupplierView', params: { id: row.id } }"
          class="inline-flex items-center gap-2 group/link"
        >
          <div class="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
            <span class="text-xs font-bold text-primary-700 dark:text-primary-300">
              {{ row.suppliername?.charAt(0)?.toUpperCase() }}
            </span>
          </div>
          <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100 group-hover/link:text-primary-600 dark:group-hover/link:text-primary-400 transition-colors">
            {{ row.suppliername }}
          </span>
        </router-link>
      </template>

      <template #cell-locationCount="{ row }">
        <span class="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">
          {{ row.locations ? row.locations.length : 0 }}
        </span>
      </template>

      <template #cell-odooStatus="{ row }">
        <span v-if="row.odooId"
          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          Linked
        </span>
        <span v-else class="text-neutral-300 dark:text-neutral-600">&mdash;</span>
      </template>

      <template #actions="{ row }">
        <router-link
          :to="{ name: 'SupplierView', params: { id: row.id } }"
          class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg transition-all duration-150 text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/30"
          title="View supplier"
          aria-label="View supplier"
        >
          <span class="material-icons text-[16px]" aria-hidden="true">open_in_new</span>
        </router-link>
      </template>
      </DataTable>
    </template>
  </div>
</template>

<script>
import { db } from "@/firebase/config";
import { collection, onSnapshot, orderBy, query } from "@firebase/firestore";
import { ref, watchEffect } from "vue";
import { useStore } from "vuex";
import DataTable from "@/components/ui/DataTable.vue";
import ErrorState from "@/components/ui/ErrorState.vue";
import AlertBanner from "@/components/ui/AlertBanner.vue";

export default {
  components: { DataTable, ErrorState, AlertBanner },
  setup() {
    const store = useStore();
    store.commit("setCurrentRoute", "Suppliers");
    const suppliers = ref(null);
    const fetchError = ref(null);

    const tableColumns = [
      { key: "suppliername", label: "Supplier", sortable: true },
      { key: "contactname", label: "Contact", sortable: true, align: "center" },
      { key: "contactemailaddress", label: "Email", sortable: true, align: "center" },
      { key: "locationCount", label: "Locations", sortable: true, align: "center" },
      { key: "geotag", label: "Geo Tag", sortable: true, align: "center" },
      { key: "odooStatus", label: "Odoo", sortable: true, align: "center" },
    ];

    let colRef = collection(db, "suppliers");
    colRef = query(colRef, orderBy("suppliername"));

    let unSub = null;
    const subscribe = () => {
      if (unSub) unSub();
      fetchError.value = null;
      unSub = onSnapshot(
        colRef,
        (snapshot) => {
          let results = [];
          snapshot.docs.forEach((doc) => {
            if (doc.data().siteid == store.state.currentSite) {
              // Flatten derived fields onto the row so DataTable sort/search
              // works on them (cell templates only affect rendering).
              results.push({
                ...doc.data(),
                id: doc.id,
                locationCount: doc.data().locations?.length ?? 0,
                odooStatus: doc.data().odooId ? "Linked" : "",
              });
            }
          });
          suppliers.value = results;
          fetchError.value = null;
        },
        (err) => {
          fetchError.value = err.message;
        }
      );
    };
    subscribe();

    watchEffect((onInvalidate) => {
      onInvalidate(() => unSub && unSub());
    });

    return { suppliers, tableColumns, fetchError, subscribe };
  },
};
</script>

<style scoped>
.suppliers-page > * {
  animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.suppliers-page > *:nth-child(1) { animation-delay: 0s; }
.suppliers-page > *:nth-child(2) { animation-delay: 0.06s; }

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .suppliers-page > * { animation: none; }
}
</style>
